import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { VENSTER, t } from '../content/content'
import { useKiosk } from '../state/store'
import { Afspeel } from './gedeeld'

/**
 * Scherm in scherm: de film van een onderdeel in een kader boven het magazijn,
 * dat zichtbaar blijft langs de randen (feedback 5 oktober). Het venster komt
 * niet als pop-up maar vloeit uit de hal op: het groeit vanuit het midden
 * terwijl de scène eromheen wegdimt, zodat het aanvoelt alsof de vlucht het
 * magazijn in overgaat in de film.
 *
 * Geen `controls`: op een kiosk zijn de browserknoppen te klein en halen ze
 * de bezoeker uit het beeld. Tikken op de film pauzeert en hervat; staat hij
 * stil, dan staat er een groot afspeelteken in beeld. Sluiten en opnieuw zijn
 * knoppen van kioskmaat. Ondertiteling komt mee als `<track>` zodra de
 * VTT-bestanden er zijn (§4.8.7).
 */
export function Videovenster() {
  const video = useKiosk((s) => s.video)
  const taal = useKiosk((s) => s.taal)
  const stopVideo = useKiosk((s) => s.stopVideo)
  const zetVideoSpeelt = useKiosk((s) => s.zetVideoSpeelt)

  return (
    <AnimatePresence>
      {video && (
        <motion.div
          key={video.src}
          className="kiosk-film"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
          transition={{ duration: 0.9 }}
        >
          <motion.figure
            className="kiosk-film-kader"
            initial={{ opacity: 0, scale: 0.72, filter: 'blur(18px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.9, filter: 'blur(12px)', transition: { duration: 0.45 } }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <Film
              src={video.src}
              ondertitel={video.ondertitels[taal]}
              taal={taal}
              onSpeelt={zetVideoSpeelt}
              onSluiten={stopVideo}
            />
            <figcaption className="kiosk-film-kop">
              <span>{t(video.titel, taal)}</span>
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Film({
  src, ondertitel, taal, onSpeelt, onSluiten,
}: {
  src: string
  ondertitel: string | null
  taal: 'nl' | 'en'
  onSpeelt: (v: boolean) => void
  onSluiten: () => void
}) {
  const ref = useRef<HTMLVideoElement>(null)
  const [afgelopen, setAfgelopen] = useState(false)
  const [fout, setFout] = useState(false)
  /** Gepauzeerd zonder afgelopen te zijn: dan staat het afspeelteken in beeld. */
  const [stil, setStil] = useState(false)

  // Een video die nog speelt bij het sluiten mag de teller niet blijven
  // tegenhouden: wat hier gemonteerd wordt, meldt zich bij het weggaan af.
  useEffect(() => () => onSpeelt(false), [onSpeelt])

  // `autoPlay` vraagt het afspelen al; play() zegt er ook bij of het mocht.
  // De kiosk-Chromium staat het altijd toe, maar Safari op een iPad weigert
  // film met geluid buiten een aanraking — en dan bleef hier een stilstaand
  // eerste beeld staan zonder dat de bezoeker wist dat hij moest tikken.
  useEffect(() => {
    ref.current?.play().catch((e: unknown) => {
      if (e instanceof DOMException && e.name === 'NotAllowedError') setStil(true)
    })
  }, [])

  const opnieuw = () => {
    const v = ref.current
    if (!v) return
    setAfgelopen(false)
    v.currentTime = 0
    void v.play()
  }

  const tik = () => {
    const v = ref.current
    if (!v || afgelopen) return
    if (v.paused) void v.play()
    else v.pause()
  }

  return (
    <>
      <div className="kiosk-film-beeld" onClick={tik}>
        {fout ? (
          <div className="kiosk-film-fout">
            {taal === 'nl' ? 'De film kon niet geladen worden.' : 'The film could not be loaded.'}
          </div>
        ) : (
          <video
            ref={ref}
            src={src}
            autoPlay
            playsInline
            preload="auto"
            onPlay={() => { setAfgelopen(false); setStil(false); onSpeelt(true) }}
            // Bij het einde vuurt `pause` vlak vóór `ended`, met `ended` al waar:
            // dan geen afspeelteken, want daar staat het eindpaneel.
            onPause={(e) => { onSpeelt(false); if (!e.currentTarget.ended) setStil(true) }}
            onEnded={() => { setAfgelopen(true); onSpeelt(false) }}
            onError={() => { setFout(true); onSpeelt(false) }}
          >
            {ondertitel && <track kind="subtitles" srcLang={taal} src={ondertitel} default />}
          </video>
        )}

        {stil && !afgelopen && !fout && (
          <button
            className="kiosk-film-speel"
            onClick={(e) => { e.stopPropagation(); void ref.current?.play() }}
            aria-label={t(VENSTER.afspelen, taal)}
          >
            <Afspeel />
          </button>
        )}

        <AnimatePresence>
          {(afgelopen || fout) && (
            <motion.div
              className="kiosk-film-einde"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {!fout && (
                <button className="kiosk-3d" onClick={(e) => { e.stopPropagation(); opnieuw() }}>
                  {t(VENSTER.opnieuw, taal)}
                </button>
              )}
              <button className="kiosk-3d kiosk-3d-vol" onClick={(e) => { e.stopPropagation(); onSluiten() }}>
                {t(VENSTER.verder, taal)}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <button className="kiosk-film-sluit" onClick={onSluiten} aria-label={t(VENSTER.sluiten, taal)}>
        <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      </button>
    </>
  )
}
